// Действия с пользователем (contracts/ui.md, «Действия и подтверждения»): архив, сброс пароля,
// завершение сеансов и новая ссылка — через окно подтверждения с последствиями в числах;
// снятие блокировки и возврат из архива — сразу, с тостом «Отменить» (ux.md, раздел 1; T134).
// «Отменить» само тоста не даёт: итог виден на месте (ux.md, раздел 2), а цепочка «Отменить»
// после «Отменить» обошла бы окно подтверждения архива. Тексты не зависят от рода человека
// и согласуют число (FR-036а, FR-036в).
//
// 041 (T021; FR-006; contracts/ui.md 041, «Пользователи → карточка»; research Р8): у владельца
// протоколов окно архива перечисляет их — «Протоколы перейдут другому владельцу: П-1, П-3» —
// и спрашивает, кто их получит: поле «Протоколы получит» с поиском пользователей (UserSearchField;
// эталон поиска — окно смены владельца 038), по умолчанию — тот, кто переносит. Внешних,
// заблокированных и самого уходящего поле не предлагает. Отказ сервера (REC-007 у ownerTo) —
// текстом под полем, окно остаётся. Тост — «Пользователь перенесён в архив. Протоколы остались
// у {ФИО} · Отменить»: «Отменить» возвращает пользователя, но не владение (plan 041, «Решения»).
// Ревью T046: протоколы в корзине сервер тоже передаёт — их число (trashedOwnedCount) окно
// называет «и N в корзине» и показывает поле, даже если других протоколов нет.
import { formatCount, nouns } from '@kp/contracts';
import type { IssuedInvite, User, UserDetail, UserListItem } from '@kp/contracts';
import { ConfirmDialog, useToast } from '@kp/ui';
import type { SearchPickerOption } from '@kp/ui';
import { KeyRound, Link2, LogOut } from '@kp/ui/icons';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useState } from 'react';
import type { RefObject } from 'react';
import { api, isApiError } from '../../api/client.ts';
import { useErrorToast } from '../../app/errors.ts';
import { countPhrase, joinPhrases } from '../../app/texts.ts';
import type { VerbForms } from '../../app/texts.ts';
import { useMe } from '../../shell/session.ts';
import { InviteLinkModal, inviteValidity } from './invite-link.tsx';
import { ownedNumbersText } from './OwnedProtocols.tsx';
import type { OwnedProtocol } from './OwnedProtocols.tsx';
import { UserSearchField, userOption } from './UserSearchField.tsx';

const WILL_END: VerbForms = ['завершится', 'завершатся'];

/** Поле окна архива владельца (041, FR-006). */
export const OWNER_TO_LABEL = 'Протоколы получит';
export const OWNER_TO_HINT = 'Любой сотрудник компании, кроме внешнего пользователя';

/**
 * «Протоколы перейдут другому владельцу: П-1, П-3» (contracts/ui.md 041); с протоколами в корзине —
 * «… П-1, П-3 и 2 в корзине», только в корзине — «… 2 в корзине» (ревью T046).
 */
export function handoverText(
  protocols: readonly Pick<OwnedProtocol, 'number'>[],
  trashed = 0,
): string {
  const numbers = protocols.length > 0 ? ownedNumbersText(protocols) : '';
  const inTrash = trashed > 0 ? `${trashed} в корзине` : '';
  const list = numbers && inTrash ? `${numbers} и ${inTrash}` : numbers || inTrash;
  return `Протоколы перейдут другому владельцу: ${list}`;
}

/** Сколько протоколов уходящего перейдёт новому владельцу — с протоколами в корзине (T046). */
export function handoverCount(
  detail: Pick<UserDetail, 'ownedProtocols' | 'trashedOwnedCount'>,
): number {
  return detail.ownedProtocols.length + detail.trashedOwnedCount;
}

/**
 * Кому можно передать протоколы (041, FR-006; research Р8): не внешнему и не заблокированному —
 * их сервер отклонит (REC-007). Архивных и самого уходящего поле не предлагает и так.
 */
export function canReceiveProtocols(user: Pick<UserListItem, 'kind' | 'state'>): boolean {
  return user.kind !== 'external' && user.state !== 'locked';
}

/**
 * «Завершатся 2 сеанса. Протоколы перейдут другому владельцу: П-1, П-3. Роли сохранятся
 * и вернутся вместе с пользователем».
 */
export function archiveDescription(
  detail: Pick<UserDetail, 'sessions' | 'ownedProtocols' | 'trashedOwnedCount'>,
): string {
  const sessions = joinPhrases([countPhrase(detail.sessions.length, WILL_END, nouns.session)]);
  return [
    sessions ? `${sessions}.` : null,
    handoverCount(detail) > 0
      ? `${handoverText(detail.ownedProtocols, detail.trashedOwnedCount)}.`
      : null,
    'Роли сохранятся и вернутся вместе с пользователем',
  ]
    .filter(Boolean)
    .join(' ');
}

/**
 * Тост архива: «Пользователь перенесён в архив»; протоколы переданы — и у кого они остаются,
 * даже после «Отменить» (contracts/ui.md 041).
 */
export function archivedText(newOwner: string | null): string {
  return newOwner
    ? `Пользователь перенесён в архив. Протоколы остались у ${newOwner}`
    : 'Пользователь перенесён в архив';
}

/** «Завершатся 2 сеанса. Блокировка снимется. Новая ссылка будет действовать 72 часа». */
export function resetDescription(detail: UserDetail): string {
  const sessions = joinPhrases([countPhrase(detail.sessions.length, WILL_END, nouns.session)]);
  return [
    sessions ? `${sessions}.` : null,
    detail.state === 'locked' ? 'Блокировка снимется.' : null,
    'Новая ссылка будет действовать 72 часа',
  ]
    .filter(Boolean)
    .join(' ');
}

export type UserDialog = 'archive' | 'reset' | 'revoke' | 'invite';

interface Issued {
  title: string;
  description: string;
  invite: IssuedInvite;
}

/** Окна подтверждения карточки и окно со ссылкой после сброса пароля или выпуска ссылки. */
export function UserActionDialogs({
  detail,
  dialog,
  onClose,
  timeZone,
  returnFocusRef,
}: {
  detail: UserDetail;
  dialog: UserDialog | null;
  onClose: () => void;
  timeZone: string;
  returnFocusRef?: RefObject<HTMLElement | null>;
}) {
  const toast = useToast();
  const showError = useErrorToast();
  const refresh = useRefreshUser();
  const restore = useRestoreUser();
  const [issued, setIssued] = useState<Issued | null>(null);
  const { id, fullName } = detail;
  const me = useMe();
  // Протоколов к передаче — и в корзине: их сервер передаёт тоже (ревью T046).
  const owned = handoverCount(detail);
  // По умолчанию протоколы получает тот, кто переносит; себя в архив — выбрать другого (FR-006).
  const defaultOwner = id === me.user.id ? null : userOption(me.user);
  const [owner, setOwner] = useState<SearchPickerOption | null>(defaultOwner);
  const [ownerError, setOwnerError] = useState<string | undefined>(undefined);
  // Окно архива открылось заново — в поле снова тот, кто переносит, без прежнего отказа.
  const [archiveShown, setArchiveShown] = useState(false);
  if ((dialog === 'archive') !== archiveShown) {
    setArchiveShown(dialog === 'archive');
    if (dialog === 'archive') {
      setOwner(defaultOwner);
      setOwnerError(undefined);
    }
  }

  /** Ошибка: окно закрывается, если повторять бессмысленно (ACC-004, REC-009), иначе остаётся. */
  const fail = (error: unknown, retry: () => void): never => {
    if (isApiError(error, 'ACC-004') || isApiError(error, 'REC-009')) {
      onClose();
      showError(error);
    } else {
      showError(error, retry);
    }
    throw error;
  };

  /** to — кто получит протоколы уходящего; null — протоколов нет, поле не нужно. */
  const archive = async (to: SearchPickerOption | null = owned > 0 ? owner : null) => {
    try {
      await api('users.archive', { params: { id }, body: to ? { ownerTo: to.value } : {} });
    } catch (error) {
      // Отказ по полю «Протоколы получит» (REC-007): текст под полем, окно остаётся (FR-006).
      if (to && isApiError(error, 'REC-007')) {
        setOwnerError(error.fields.ownerTo ?? `${error.message}. ${error.nextStep}`);
        throw error;
      }
      fail(error, () => void archive(to).catch(() => undefined));
    }
    await refresh();
    toast.ok(archivedText(to?.label ?? null), {
      action: { label: 'Отменить', onClick: () => void restore(id, { undo: true }) },
      // Пояснение о протоколах — важное: тост живёт дольше (README «Тосты»).
      ...(to ? { duration: 'long' as const } : {}),
    });
  };

  const reset = async () => {
    let invite: IssuedInvite;
    try {
      invite = await api('users.resetPassword', { params: { id } });
    } catch (error) {
      return fail(error, () => void reset().catch(() => undefined));
    }
    await refresh();
    setIssued({
      title: 'Пароль сброшен',
      description: `Перешлите ссылку: по ней ${fullName} задаст новый пароль. ${inviteValidity(invite, timeZone)}`,
      invite,
    });
  };

  const revoke = async () => {
    try {
      await api('users.revokeSessions', { params: { id } });
    } catch (error) {
      fail(error, () => void revoke().catch(() => undefined));
    }
    await refresh();
    toast.ok('Сеансы завершены');
  };

  const issue = async () => {
    let invite: IssuedInvite;
    try {
      invite = await api('users.invite', { params: { id } });
    } catch (error) {
      return fail(error, () => void issue().catch(() => undefined));
    }
    await refresh();
    setIssued({
      title: 'Новая ссылка-приглашение',
      description: `По этой ссылке ${fullName} задаст пароль сам. ${inviteValidity(invite, timeZone)}`,
      invite,
    });
  };

  const sessions = detail.sessions.length;
  const change = (open: boolean) => {
    if (!open) onClose();
  };

  return (
    <>
      <ConfirmDialog
        open={dialog === 'archive'}
        onOpenChange={change}
        tone="warn"
        title={`Перенести в архив: ${fullName}?`}
        description={archiveDescription(detail)}
        confirmLabel="Перенести в архив"
        onConfirm={() => archive()}
        confirmDisabled={owned > 0 && owner === null}
        returnFocusRef={returnFocusRef}
      >
        {owned > 0 ? (
          <UserSearchField
            label={OWNER_TO_LABEL}
            value={owner}
            onChange={(option) => {
              setOwner(option);
              setOwnerError(undefined);
            }}
            excludeId={id}
            allow={canReceiveProtocols}
            error={ownerError}
            hint={OWNER_TO_HINT}
            required
          />
        ) : null}
      </ConfirmDialog>
      <ConfirmDialog
        open={dialog === 'reset'}
        onOpenChange={change}
        tone="warn"
        icon={KeyRound}
        title={`Сбросить пароль: ${fullName}?`}
        description={resetDescription(detail)}
        confirmLabel="Сбросить пароль"
        onConfirm={reset}
        returnFocusRef={returnFocusRef}
      />
      <ConfirmDialog
        open={dialog === 'revoke'}
        onOpenChange={change}
        tone="warn"
        icon={LogOut}
        title={`Завершить ${formatCount(sessions, nouns.session)}: ${fullName}?`}
        description="Войти снова придётся на каждом устройстве"
        confirmLabel="Завершить сеансы"
        onConfirm={revoke}
        returnFocusRef={returnFocusRef}
      />
      <ConfirmDialog
        open={dialog === 'invite'}
        onOpenChange={change}
        tone="warn"
        icon={Link2}
        title={`Выпустить новую ссылку для ${fullName}?`}
        description="Прежняя ссылка перестанет работать"
        confirmLabel="Выпустить ссылку"
        onConfirm={issue}
        returnFocusRef={returnFocusRef}
      />
      <InviteLinkModal
        open={issued !== null}
        onOpenChange={(open) => {
          if (!open) setIssued(null);
        }}
        title={issued?.title ?? ''}
        description={issued?.description ?? ''}
        invite={issued?.invite ?? null}
        returnFocusRef={returnFocusRef}
      />
    </>
  );
}

/** Перечитать карточку и список после действия. */
export function useRefreshUser(): () => Promise<void> {
  const queryClient = useQueryClient();
  return useCallback(async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['api', 'users.get'] }),
      queryClient.invalidateQueries({ queryKey: ['api', 'users.list'] }),
    ]);
  }, [queryClient]);
}

export interface RestoreOptions {
  /** «Отменить» в тосте архива: пользователь возвращается без своего тоста — итог виден на месте. */
  undo?: boolean;
}

/**
 * Возврат из архива — сразу, тост «Пользователь возвращён · Отменить» (FR-026б, FR-026в; T134).
 * «Отменить» переносит пользователя обратно в архив без окна: у только что возвращённого нет
 * сеансов, а владение протоколами к нему с возвратом не вернулось (041) — последствий, которые
 * называло бы окно архива, нет (FR-026в). true — пользователь возвращён.
 */
export function useRestoreUser(): (id: string, options?: RestoreOptions) => Promise<boolean> {
  const toast = useToast();
  const showError = useErrorToast();
  const refresh = useRefreshUser();
  /** «Отменить» возврата: снова в архив; ошибка — тост с причиной и кодом. */
  const archiveBack = useCallback(
    async function archiveBack(id: string): Promise<void> {
      try {
        await api('users.archive', { params: { id }, body: {} });
      } catch (error) {
        showError(error, () => void archiveBack(id));
      } finally {
        await refresh();
      }
    },
    [showError, refresh],
  );
  return useCallback(
    async function restore(id: string, options: RestoreOptions = {}): Promise<boolean> {
      try {
        await api('users.restore', { params: { id } });
      } catch (error) {
        showError(error, () => void restore(id, options));
        return false;
      }
      await refresh();
      if (!options.undo) {
        toast.ok('Пользователь возвращён', {
          action: { label: 'Отменить', onClick: () => void archiveBack(id) },
        });
      }
      return true;
    },
    [toast, showError, refresh, archiveBack],
  );
}

/**
 * Отказ «Отменить» снятия блокировки по состоянию записи: запись изменили после снятия (REC-001)
 * или пользователя перенесли в архив (REC-009). Тот же запрос получит тот же отказ — тост без
 * «Повторить»; прочие сбои (нет связи, сбой сервера) — с «Повторить» той же отмены (T155).
 */
export function isUnlockUndoRefusal(error: unknown): boolean {
  return isApiError(error, 'REC-001') || isApiError(error, 'REC-009');
}

/**
 * Снять блокировку — сразу, тост «Блокировка снята · Отменить» (FR-027а; T134). «Отменить»
 * возвращает блокировку до прежнего срока (POST /api/users/{id}/unlock/undo) с версией из ответа
 * снятия: если запись успели изменить — отказ REC-001 тостом, блокировка не возвращается. Отмена
 * не сохранилась — тост-ошибка с «Повторить» той же отмены (ux.md, раздел 2; T155).
 */
export function useUnlockUser(): (id: string) => Promise<void> {
  const toast = useToast();
  const showError = useErrorToast();
  const refresh = useRefreshUser();
  const undoUnlock = useCallback(
    async function undoUnlock(user: User): Promise<void> {
      try {
        await api('users.unlockUndo', { params: { id: user.id }, version: user.version });
      } catch (error) {
        showError(error, isUnlockUndoRefusal(error) ? undefined : () => void undoUnlock(user));
      } finally {
        await refresh();
      }
    },
    [showError, refresh],
  );
  return useCallback(
    async function unlock(id: string): Promise<void> {
      let unlocked: User;
      try {
        unlocked = await api('users.unlock', { params: { id } });
      } catch (error) {
        showError(error, () => void unlock(id));
        return;
      }
      await refresh();
      toast.ok('Блокировка снята', {
        action: { label: 'Отменить', onClick: () => void undoUnlock(unlocked) },
      });
    },
    [toast, showError, refresh, undoUnlock],
  );
}
